import { memo } from 'react';
import { useChatStore } from '@/stores/agents/chat-store';
import { DECOMPOSER_INTENT_ID, SYNTHESIZER_INTENT_ID } from '@/types/agent-socket';
import { INK } from '@/styles/tokens';

interface LiveStatusLineProps {
  messageId: string;
}

const isSystemIntentId = (id: string | null): boolean =>
  id === DECOMPOSER_INTENT_ID || id === SYNTHESIZER_INTENT_ID;

const LiveStatusLineImpl: React.FC<LiveStatusLineProps> = ({ messageId }) => {
  const status = useChatStore((s) => s.liveStatus[messageId]);
  if (!status) return null;

  const realIntents = status.intents.filter((i) => !isSystemIntentId(i.id));
  const total = realIntents.length;
  const done = realIntents.filter((i) => i.status !== 'pending').length;

  const doneLabels = realIntents
    .filter((i) => i.status !== 'pending')
    .map((i) => {
      const tick = i.status === 'failed' ? '✗' : '✓';
      const label = i.text.length > 30 ? `${i.text.slice(0, 30)}…` : i.text;
      return `${tick} ${label}`;
    })
    .join(', ');

  const current = status.synthesizing ? 'Composing answer…' : status.currentActivity;

  let text: string;
  if (total > 1) {
    const parts: string[] = [`(${done}/${total})`];
    if (doneLabels) parts.push(doneLabels);
    if (current) parts.push(`• ${current}`);
    text = parts.join(' ');
  } else {
    text = current || 'Thinking…';
  }

  return (
    <div
      style={{
        color: INK[500],
        fontSize: 13,
        fontStyle: 'italic',
        padding: '4px 12px',
        lineHeight: 1.4,
        transition: 'opacity 150ms ease',
      }}
    >
      {text}
    </div>
  );
};

export const LiveStatusLine = memo(LiveStatusLineImpl);
